import { useState, useEffect, useRef } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useFormikContext } from 'formik';
import { GoogleMapsService } from '@/map/application/services/googleMapsService';
import { PlaceDetails } from '@/map/domain/entities/map.types';
import { LocationFormData } from '../validationSchema';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';

export type UseLocationFormPanelProps = {
  onPlaceSelect: (place: google.maps.places.Place) => void;
  setMarkerPosition: (position: google.maps.LatLngLiteral) => void;
  markerPosition: google.maps.LatLngLiteral | null;
  updateFormValues: (details: PlaceDetails) => Promise<void>;
  hideCoordinates?: boolean;
  hideCountry?: boolean;
  panToWithOffset?: (position: google.maps.LatLngLiteral) => void;
};

export const useLocationFormPanel = ({
  onPlaceSelect,
  setMarkerPosition,
  markerPosition,
  updateFormValues,
  hideCoordinates = false,
  hideCountry = false,
  panToWithOffset
}: UseLocationFormPanelProps) => {
  const map = useMap();
  const placesLibrary = useMapsLibrary('places');
  const formik = useFormikContext<LocationFormData>();
  const { showErrorWithDetails } = useSnackbar();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasAutoOpenedRef = useRef(false);
  const hasInitialSearchValueRef = useRef(false);
  const googleMapsService = new GoogleMapsService();

  useEffect(() => {
    if (!placesLibrary) return;
    setSessionToken(new google.maps.places.AutocompleteSessionToken());
  }, [placesLibrary]);

  // Pre-fill Location Search when form has existing address data (e.g. edit job site modal)
  useEffect(() => {
    if (hasInitialSearchValueRef.current) return;
    const { address, city, state, zipCode } = formik.values;
    const parts = [address, city, state, zipCode].filter((s) => s != null && String(s).trim() !== '');
    if (parts.length > 0) {
      setInputValue(parts.join(', '));
      hasInitialSearchValueRef.current = true;
    }
  }, [formik.values]);

  // Open details panel when marker position is set from outside (e.g., context menu)
  useEffect(() => {
    if (markerPosition) {
      setIsDetailsOpen(true);
    }
  }, [markerPosition]);

  // Auto-open details panel when there's existing address data or coordinates (only on initial load)
  useEffect(() => {
    const { address, city, state, zipCode, latitude, longitude } = formik.values;
    const hasAddressData = address || city || state || zipCode;
    const hasCoordinates = latitude != null && longitude != null;

    // Open panel if we have address data or coordinates, but only once on initial load
    if ((hasAddressData || hasCoordinates) && !hasAutoOpenedRef.current) {
      setIsDetailsOpen(true);
      hasAutoOpenedRef.current = true;
    }
  }, [formik.values]);

  // Auto-expand panel when there are validation errors in location fields
  useEffect(() => {
    const { errors, submitCount } = formik;

    // Only check for errors if form has been submitted at least once
    if (submitCount === 0) {
      return;
    }

    // List of location form fields to check for errors
    const locationFields = ['address', 'city', 'state', 'zipCode'];

    // Add optional fields if they're not hidden
    if (!hideCountry) {
      locationFields.push('country');
    }
    if (!hideCoordinates) {
      locationFields.push('latitude', 'longitude');
    }

    // Check if any location field has an error (after form submission, errors are available regardless of touched state)
    const hasLocationErrors = locationFields.some((field) => {
      const fieldError = errors[field as keyof typeof errors];
      return fieldError !== undefined && fieldError !== null && fieldError !== '';
    });

    // Expand panel if there are errors and panel is closed
    if (hasLocationErrors && !isDetailsOpen) {
      setIsDetailsOpen(true);
    }
  }, [formik.errors, formik.submitCount, isDetailsOpen, hideCoordinates, hideCountry]);

  const handleInputChange = async (value: string) => {
    setInputValue(value);

    const googleMapsCoords = googleMapsService.parseGoogleMapsUrl(value);
    if (googleMapsCoords) {
      await googleMapsService.handleGoogleMapsUrl(googleMapsCoords, map, setMarkerPosition, updateFormValues);
      setSuggestions([]);
      return;
    }

    const coordinates = googleMapsService.parseCoordinates(value);
    if (coordinates) {
      await googleMapsService.handleDirectCoordinates(coordinates, map, setMarkerPosition, updateFormValues);
      setSuggestions([]);
      return;
    }

    if (!placesLibrary || !value) {
      setSuggestions([]);
      return;
    }

    await googleMapsService.handlePlacesApiSearch(value, map, sessionToken, setIsLoading, setSuggestions);
  };

  const handlePlaceSelect = async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    if (!suggestion.placePrediction) return;
    try {
      setIsLoading(true);
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'addressComponents']
      });

      if (place.location && map) {
        const position = { lat: place.location.lat(), lng: place.location.lng() };
        if (panToWithOffset) {
          panToWithOffset(position);
        } else {
          map.panTo(place.location);
        }
        map.setZoom(15);
        onPlaceSelect(place);
        setIsDetailsOpen(true);
      }

      setInputValue(place.displayName ?? '');
      setSuggestions([]);
    } catch (error) {
      console.error('Error selecting place:', error);
      showErrorWithDetails(error, 'Failed to load place details. Please try selecting the place again.', 'Error Selecting Place');
      // Reset state to prevent UI from being stuck
      setIsLoading(false);
      setSuggestions([]);
      // Keep input value so user can try again
    } finally {
      setIsLoading(false);
    }
  };

  const handleCoordinatesChange = (field: 'latitude' | 'longitude', value: number) => {
    formik.setFieldValue(field, value);

    // Update map position if both coordinates are available
    const currentLat = field === 'latitude' ? value : formik.values.latitude;
    const currentLng = field === 'longitude' ? value : formik.values.longitude;

    if (map && currentLat != null && currentLng != null) {
      const newPosition = {
        lat: currentLat,
        lng: currentLng
      };
      if (panToWithOffset) {
        panToWithOffset(newPosition);
      } else {
        map.panTo(newPosition);
      }
      setMarkerPosition(newPosition);
    }
  };

  return {
    inputValue,
    suggestions,
    isLoading,
    isDetailsOpen,
    setIsDetailsOpen,
    handleInputChange,
    handlePlaceSelect,
    handleCoordinatesChange,
    formik
  };
};
